/**
 * ============================================================================
 * Album Page Section Script
 * ============================================================================
 *
 * Handles:
 * - Initialization of all album-page sections
 * - GSAP-powered image slider (drag, wheel, arrows, snapping)
 * - Featured video mute/unmute toggle
 *
 * Dependencies:
 * - GSAP
 * - GSAP Draggable
 * ============================================================================
 */

/**
 * Initialize all sections on page load
 */
document.addEventListener("DOMContentLoaded", () => {
  const albumSections = document.querySelectorAll(".album-page");

  if (!albumSections.length) return;

  albumSections.forEach((section) => {
    initializeAlbumPage(section);
  });
});

/**
 * Re-initialize sections when loaded in Shopify Theme Editor
 */
document.addEventListener("shopify:section:load", (event) => {
  const section = event.target.querySelector(".album-page") || event.target;

  if (
    section &&
    section.classList &&
    section.classList.contains("album-page")
  ) {
    initializeAlbumPage(section);
  }
});

/**
 * Apply header color state for album pages
 */
function initializeAlbumHeaderState(section) {
  if (section.dataset.useLightHeader === "true") {
    document.body.classList.add("album-page-light-header");
  } else {
    document.body.classList.remove("album-page-light-header");
  }
}

/**
 * Initialize one album page section instance
 * Prevents double initialization via dataset flag
 */
function initializeAlbumPage(section) {
  if (section.dataset.albumPageInitialized === "true") return;
  section.dataset.albumPageInitialized = "true";

  initializeAlbumHeaderState(section);

  const slider = section.querySelector("[data-slider]");
  const sliderTrack = section.querySelector("[data-slider-track]");

  if (slider && sliderTrack) {
    initializeSlider(section, slider, sliderTrack);
  }

  initializeFeaturedVideo(section);
  initializeAlbumLightbox(section);
}

/**
 * ============================================================================
 * SLIDER (GSAP + DRAGGABLE)
 * ============================================================================
 *
 * Features:
 * - Drag (mouse + touch)
 * - Mouse wheel horizontal scroll
 * - Arrow navigation
 * - Snap to nearest slide
 * - Responsive rebuild on resize
 * ============================================================================
 */
function initializeSlider(section, slider, sliderTrack) {
  if (
    slider.classList.contains("album-page__slider--display-grid") &&
    window.innerWidth >= 750
  )
    return;
  if (typeof gsap === "undefined" || typeof Draggable === "undefined") return;

  const viewport = slider.querySelector("[data-slider-viewport]");
  const prevBtn = slider.querySelector("[data-slider-prev]");
  const nextBtn = slider.querySelector("[data-slider-next]");
  const slides = Array.from(sliderTrack.querySelectorAll(".album-page__slide"));

  if (!viewport || !slides.length) return;

  // === STATE ===
  let draggableInstance = null;
  let currentX = 0; // actual position
  let targetX = 0; // where we are animating toward
  let minX = 0;
  let maxX = 0;
  let snapPositions = [];

  // wheel behavior
  let wheelTween = null;
  let wheelSnapTimeout = null;

  // resize optimization
  let resizeRaf = null;

  /**
   * Clamp helper
   */
  function clamp(value, min, max) {
    return Math.max(min, Math.min(max, value));
  }

  /**
   * Calculate slider bounds + snap positions
   */
  function buildSnapPositions() {
    const viewportWidth = viewport.offsetWidth;
    const trackWidth = sliderTrack.scrollWidth;

    maxX = 0;
    minX = Math.min(0, viewportWidth - trackWidth);

    snapPositions = slides.map((slide) => {
      const rawX = -slide.offsetLeft;
      return clamp(rawX, minX, maxX);
    });

    // remove duplicates
    snapPositions = [...new Set(snapPositions)];
  }

  /**
   * Enable / disable arrows based on position
   */
  function updateArrowState() {
    if (prevBtn) prevBtn.disabled = currentX >= maxX;
    if (nextBtn) nextBtn.disabled = currentX <= minX;
  }

  /**
   * Move slider to X position
   * @param {number} x
   * @param {boolean} animate
   * @param {number} duration
   */
  function setTrackX(x, animate = true, duration = 0.5) {
    currentX = clamp(x, minX, maxX);
    targetX = currentX;

    // stop wheel animation if active
    if (wheelTween) {
      wheelTween.kill();
      wheelTween = null;
    }

    if (animate) {
      gsap.to(sliderTrack, {
        x: currentX,
        duration,
        ease: "power3.out",
        overwrite: true,
        onUpdate: () => {
          currentX = Number(gsap.getProperty(sliderTrack, "x"));
          updateArrowState();
        },
      });
    } else {
      gsap.set(sliderTrack, { x: currentX });
      updateArrowState();
    }

    if (draggableInstance) {
      draggableInstance.update();
    }
  }

  /**
   * Find closest slide index to current X
   */
  function findNearestSnapIndex(x) {
    let nearestIndex = 0;
    let nearestDistance = Infinity;

    snapPositions.forEach((pos, index) => {
      const distance = Math.abs(pos - x);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearestIndex = index;
      }
    });

    return nearestIndex;
  }

  /**
   * Snap to nearest slide
   */
  function goToNearestSnap(x) {
    if (!snapPositions.length) return;
    const index = findNearestSnapIndex(x);
    setTrackX(snapPositions[index], true, 0.5);
  }

  /**
   * Arrow navigation
   */
  function goToNext() {
    if (!snapPositions.length) return;

    const currentIndex = findNearestSnapIndex(currentX);
    const nextIndex = Math.min(currentIndex + 1, snapPositions.length - 1);

    setTrackX(snapPositions[nextIndex], true, 0.5);
  }

  function goToPrev() {
    if (!snapPositions.length) return;

    const currentIndex = findNearestSnapIndex(currentX);
    const prevIndex = Math.max(currentIndex - 1, 0);

    setTrackX(snapPositions[prevIndex], true, 0.5);
  }

  /**
   * Draggable setup (drag/swipe)
   */
  function setupDraggable() {
    if (draggableInstance) {
      draggableInstance.kill();
    }

    draggableInstance = Draggable.create(sliderTrack, {
      type: "x",
      inertia: false,
      edgeResistance: 0.9,
      bounds: { minX, maxX },

      // stop wheel behavior while dragging
      onPress: function () {
        if (wheelTween) wheelTween.kill();
        if (wheelSnapTimeout) clearTimeout(wheelSnapTimeout);
      },

      onDrag: function () {
        currentX = this.x;
        targetX = this.x;
        updateArrowState();
      },

      onThrowUpdate: function () {
        currentX = this.x;
        targetX = this.x;
        updateArrowState();
      },

      onDragEnd: function () {
        goToNearestSnap(this.x);
      },
    })[0];
  }

  /**
   * Mouse wheel horizontal scroll (desktop only)
   * Smooth movement + delayed snap
   */
  function handleWheel(event) {
    if (window.innerWidth < 750) return;
    if (minX === 0) return;

    event.preventDefault();

    const delta = event.deltaY || event.deltaX;

    // adjust sensitivity here
    targetX = clamp(targetX - delta * 0.9, minX, maxX);

    if (wheelTween) wheelTween.kill();

    wheelTween = gsap.to(sliderTrack, {
      x: targetX,
      duration: 0.45,
      ease: "power3.out",
      overwrite: true,
      onUpdate: () => {
        currentX = Number(gsap.getProperty(sliderTrack, "x"));
        updateArrowState();
      },
    });

    // snap after user stops scrolling
    if (wheelSnapTimeout) clearTimeout(wheelSnapTimeout);

    wheelSnapTimeout = setTimeout(() => {
      currentX = Number(gsap.getProperty(sliderTrack, "x"));
      goToNearestSnap(currentX);
    }, 140);
  }

  /**
   * Rebuild slider on resize
   */
  function rebuildSlider() {
    buildSnapPositions();
    setupDraggable();
    setTrackX(currentX, false);
  }

  function handleResize() {
    if (resizeRaf) cancelAnimationFrame(resizeRaf);

    resizeRaf = requestAnimationFrame(() => {
      rebuildSlider();
    });
  }

  /**
   * Event bindings
   */
  prevBtn?.addEventListener("click", goToPrev);
  nextBtn?.addEventListener("click", goToNext);
  viewport.addEventListener("wheel", handleWheel, { passive: false });
  window.addEventListener("resize", handleResize);

  /**
   * Initial build
   */
  rebuildSlider();
}

/**
 * ============================================================================
 * FEATURED VIDEO
 * ============================================================================
 *
 * Handles:
 * - Mute / unmute toggle
 * - Sync button state with video state
 * ============================================================================
 */
function initializeFeaturedVideo(section) {
  const video = section.querySelector(".album-page__featured-video");
  const toggleButton = section.querySelector("[data-video-sound-toggle]");

  if (!video || !toggleButton) return;

  updateVideoToggleState(video, toggleButton);

  toggleButton.addEventListener("click", () => {
    video.muted = !video.muted;
    updateVideoToggleState(video, toggleButton);
  });
}

/**
 * Sync button UI with video state
 */
function updateVideoToggleState(video, button) {
  const isMuted = video.muted;
  button.classList.toggle("is-muted", isMuted);
  button.setAttribute("aria-label", isMuted ? "Unmute video" : "Mute video");
}

function initializeAlbumLightbox(section) {
  const lightbox = section.querySelector("[data-album-lightbox]");
  const lightboxImage = section.querySelector("[data-album-lightbox-image]");
  const closeButton = section.querySelector("[data-album-lightbox-close]");
  const prevButton = section.querySelector("[data-album-lightbox-prev]");
  const nextButton = section.querySelector("[data-album-lightbox-next]");
  const triggers = Array.from(
    section.querySelectorAll("[data-album-lightbox-trigger]"),
  );

  if (!lightbox || !lightboxImage || !triggers.length) return;

  let currentIndex = 0;

  function openLightbox(index) {
    const trigger = triggers[index];
    if (!trigger) return;

    currentIndex = index;
    lightboxImage.src = trigger.dataset.lightboxImage;
    lightbox.classList.add("is-open");
    lightbox.setAttribute("aria-hidden", "false");
    document.body.style.overflow = "hidden";
  }

  function closeLightbox() {
    lightbox.classList.remove("is-open");
    lightbox.setAttribute("aria-hidden", "true");
    lightboxImage.src = "";
    document.body.style.overflow = "";
  }

  function goToNextImage() {
    currentIndex = (currentIndex + 1) % triggers.length;
    openLightbox(currentIndex);
  }

  function goToPrevImage() {
    currentIndex = (currentIndex - 1 + triggers.length) % triggers.length;
    openLightbox(currentIndex);
  }

  triggers.forEach((trigger, index) => {
    trigger.addEventListener("click", () => {
      openLightbox(index);
    });
  });

  closeButton?.addEventListener("click", closeLightbox);
  nextButton?.addEventListener("click", goToNextImage);
  prevButton?.addEventListener("click", goToPrevImage);

  lightbox.addEventListener("click", (event) => {
    if (event.target === lightbox) {
      closeLightbox();
    }
  });

  document.addEventListener("keydown", (event) => {
    if (!lightbox.classList.contains("is-open")) return;

    if (event.key === "Escape") closeLightbox();
    if (event.key === "ArrowRight") goToNextImage();
    if (event.key === "ArrowLeft") goToPrevImage();
  });
}
